import { usePrevious } from '@ps/web-shared-components';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { select } from '@swf/core/store';

import ChestIcon from '../ChestIcon/ChestIcon';
import Nudge from '../Nudge/Nudge';

import styles from './CircularProgressBar.scss';

interface Props {
  size: number;
  expiring?: boolean;
  showNudge?: boolean;
  strokeWidth: number;
  pointToChestInventory?: boolean;
}

const CircularProgressBar: React.FC<Props> = (props) => {
  const history = useHistory();
  const { tierId, chestsCount, currentPoints, goingToTierDown, targetPoints } = useSelector(
    select.rewards.getCvlBarData
  );
  const percentage = (currentPoints / targetPoints) * 100 || 0;
  const previousValue = usePrevious(percentage);
  const [color, setColor] = useState('');
  const [progress, setProgress] = useState(percentage || 0.3);
  const [isAnimated, setIsAnimated] = useState(true);

  const viewBox = `0 0 ${props.size} ${props.size}`;
  const radius = (props.size - props.strokeWidth) / 2;
  const circumference = radius * Math.PI * 2;
  const dash = (progress * circumference) / 100;

  useEffect(() => {
    if (!props.pointToChestInventory) {
      setProgress(percentage);
      setColor(
        percentage === 0 ? '#666' : !!goingToTierDown || props.expiring ? '#CC3600' : '#02BD9C'
      );
    }
  }, [percentage, goingToTierDown, props.expiring, props.pointToChestInventory]);

  useEffect(() => {
    if (!props.pointToChestInventory) {
      setIsAnimated(!(previousValue === 0 || percentage < previousValue));
    }
  }, [percentage, previousValue, props.pointToChestInventory]);

  const handleClick = useCallback(() => {
    history.push({ pathname: 'prhub/my-rewards/chests' });
  }, [history]);

  return (
    <div
      className={styles.wrapper}
      data-testid={`${props.pointToChestInventory ? 'chest' : 'loyalty'}-widget`}
      style={{ width: `${props.size}px` }}
      onClick={() => props.pointToChestInventory && handleClick()}
    >
      <svg width={props.size} height={props.size} viewBox={viewBox}>
        <circle
          r={radius}
          fill="none"
          stroke="#E0E0E0"
          cx={props.size / 2}
          cy={props.size / 2}
          strokeWidth={`${props.strokeWidth}px`}
        />
        <circle
          r={radius}
          fill="none"
          stroke={color}
          cx={props.size / 2}
          cy={props.size / 2}
          style={{ transition: isAnimated ? 'all 0.5s' : 'none' }}
          strokeWidth={`${props.strokeWidth}px`}
          transform={`rotate(-90 ${props.size / 2} ${props.size / 2})`}
          strokeDasharray={`${dash},${circumference - dash}`}
        />
      </svg>
      <div className={styles.chestIcon}>
        <ChestIcon
          tier={tierId}
          style={{ transform: 'translateX(-29%) translateY(-19%) scale(0.26)' }}
        />
      </div>
      <div className={styles.nudge}>
        {!!props.showNudge && <Nudge badgeContent={chestsCount || 0} />}
      </div>
    </div>
  );
};

export default CircularProgressBar;
